/**
 * Node Renderer Component
 *
 * Renders the appropriate player view based on node type.
 */

import type { FlowContext, FlowPlayer, NodeState } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { AutoBalanceView } from "../nodes/auto-balance/PlayerView";
import { CaptainDraftView } from "../nodes/captain-draft/PlayerView";
import { CharacterBanView } from "../nodes/character-ban/PlayerView";
import { CharacterPickView } from "../nodes/character-pick/PlayerView";
import { GenericNodeView } from "../nodes/GenericNodeView";
import { GameModeSelectView } from "../nodes/game-mode-select/PlayerView";
import { HostInstructionsView } from "../nodes/host-instructions/PlayerView";
import { LobbyCodeInputView } from "../nodes/lobby-code-input/PlayerView";
import { LobbyCreatedView } from "../nodes/lobby-created/PlayerView";
import { MapBanView } from "../nodes/map-ban/PlayerView";
import { MapVoteView } from "../nodes/map-vote/PlayerView";
import { MatchCancelledView } from "../nodes/match-cancelled/PlayerView";
import { MatchStartView } from "../nodes/match-start/PlayerView";
import { ReadyUpView } from "../nodes/ready-up/PlayerView";
import { SelfSelectView } from "../nodes/self-select/PlayerView";
import { SideSelectView } from "../nodes/side-select/PlayerView";
import { TeamVoiceSplitView } from "../nodes/team-voice-split/PlayerView";
import { VoiceChannelJoinView } from "../nodes/voice-channel-join/PlayerView";

type NodeRendererProps = {
  nodeState: NodeState;
  players: FlowPlayer[];
  context: FlowContext;
  onAction: (type: string, payload: unknown) => Promise<{ success: boolean; error?: string }>;
};

export function NodeRenderer({ nodeState, players, context, onAction }: NodeRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.focus();
  }, [nodeState.nodeId]);

  const commonProps = {
    nodeState: nodeState as never,
    players,
    context,
    onAction,
  };

  const renderNode = () => {
    switch (nodeState.nodeType) {
      case "ready_up":
        return <ReadyUpView {...commonProps} />;
      case "map_vote":
        return <MapVoteView {...commonProps} />;
      case "map_ban":
        return <MapBanView {...commonProps} />;
      case "auto_balance":
        return <AutoBalanceView {...commonProps} />;
      case "captain_draft":
        return <CaptainDraftView {...commonProps} />;
      case "self_select":
        return <SelfSelectView {...commonProps} />;
      case "match_start":
        return <MatchStartView {...commonProps} />;
      case "match_cancelled":
        return <MatchCancelledView {...commonProps} />;
      case "game_mode_select":
        return <GameModeSelectView {...commonProps} />;
      case "character_ban":
        return <CharacterBanView {...commonProps} />;
      case "character_pick":
        return <CharacterPickView {...commonProps} />;
      case "side_select":
        return <SideSelectView {...commonProps} />;
      case "host_instructions":
        return <HostInstructionsView {...commonProps} />;
      case "lobby_code_input":
        return <LobbyCodeInputView {...commonProps} />;
      case "lobby_created":
        return <LobbyCreatedView {...commonProps} />;
      case "voice_channel_join":
        return <VoiceChannelJoinView {...commonProps} />;
      case "team_voice_split":
        return <TeamVoiceSplitView {...commonProps} />;
      default:
        return <GenericNodeView {...commonProps} />;
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={nodeState.nodeId}
        ref={containerRef}
        tabIndex={-1}
        className="outline-none"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        {renderNode()}
      </motion.div>
    </AnimatePresence>
  );
}
